import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Mic, ArrowUpRight } from "lucide-react";
import { motion } from 'framer-motion';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';
import API from "../lib/api";

export default function Home() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  // ✅ CHECK LOGIN
  const isLoggedIn = !!localStorage.getItem("token");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    localStorage.removeItem("admin_token");

    navigate("/login");
  };

  const handleBuildNow = async () => {
    console.log("🔥 BUTTON CLICKED");

    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (!prompt.trim()) return;

    try {
      const token = localStorage.getItem("token");

      const res = await API.post(
        "/builds",
        { prompt },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("✅ RESPONSE:", res.data);

      // ✅ IMPORTANT CHANGE HERE
      navigate(`/build-status/${res.data.buildId}`);

    } catch (err) {
      console.error("❌ ERROR:", err);
    }
  };

  // const handleBuildNow = () => {
  //   if (prompt.trim()) {
  //     navigate('/build-status', { state: { prompt } });
  //   }
  // };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && prompt.trim()) {
      handleBuildNow();
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* ✅ SHOW SIDEBAR ONLY IF LOGGED IN */}

      {isLoggedIn && <Sidebar />}
      <div className={isLoggedIn ? "md:ml-64" : ""}>
        <div className="flex items-center justify-between p-4">
          <TopBar />

          {isLoggedIn && (
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all"
            >
              Logout
            </button>
          )}
        </div>

        <main className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial from-primary/10 via-transparent to-transparent pointer-events-none"></div>

          <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 py-12 relative z-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center space-y-8 max-w-4xl w-full"
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium"
                data-testid="hero-badge"
              >
                <Sparkles className="w-4 h-4" />
                AI-Powered App & Website Builder
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="hero-title"
              >
                Transform Ideas Into
                <br />
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Real Products
                </span>
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto"
                data-testid="hero-subtitle"
              >
                Describe your vision and watch as AI creates production-ready websites, admin panels, and mobile apps instantly.
              </motion.p>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="space-y-4"
              >
                <div className="relative w-full max-w-4xl mx-auto group">

                  {/* Glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-accent/30 rounded-[30px] blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-300"></div>

                  {/* Main Prompt Box */}
                  <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-[32px] shadow-2xl transition-all duration-300 hover:border-primary/20">


                    {/* Textarea */}
                    <textarea
                      rows={1}
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value);

                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleBuildNow();
                        }
                      }}
                      placeholder="Describe the app, website or dashboard you want to build..."
                      className="
    w-full
    min-h-[160px]
    max-h-[420px]
    bg-transparent
    resize-none
    border-none
    outline-none
    overflow-y-auto
    px-6
    pt-6
    pb-6
    text-[15px]
    md:text-base
    leading-7
    text-white
    placeholder:text-white/30
  "
                    />

                    {/* Bottom Actions */}
                    <div className="flex items-center justify-between px-6 pb-5 pt-2">


                      {/* Left Side */}
                      <div className="flex items-center gap-3">

                        {/* AI Badge */}
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">
                          <Sparkles className="w-4 h-4 text-primary" />
                          AI Builder
                        </div>

                        {/* Voice Button */}
                        <button
                          className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all"
                        >
                          <Mic className="w-5 h-5 text-white/80" />
                        </button>

                      </div>


                      {/* Build Button */}
                      <button
                        onClick={handleBuildNow}
                        disabled={!prompt.trim()}
                        className="px-6 py-3 rounded-2xl bg-gradient-to-r from-primary to-accent text-white font-semibold shadow-lg hover:scale-105 transition-all disabled:opacity-40"
                      >
                        Build Now →
                      </button>

                    </div>
                  </div>
                </div>

                {/* <motion.button
                  onClick={handleBuildNow}
                  onHoverStart={() => setIsHovered(true)}
                  onHoverEnd={() => setIsHovered(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!prompt.trim()}
                  data-testid="build-now-button"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-primary to-accent text-white font-semibold text-lg shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className={`w-5 h-5 ${isHovered ? 'animate-spin' : ''}`} />
                  Build Now
                </motion.button> */}
              </motion.div>

              {/* <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="grid grid-cols-3 gap-8 pt-12 max-w-2xl mx-auto"
                data-testid="hero-stats"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>10K+</div>
                  <div className="text-sm text-muted-foreground">Apps Built</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>2.5s</div>
                  <div className="text-sm text-muted-foreground">Avg Build Time</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>99.9%</div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
              </motion.div> */}
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
}
